import React, { useState } from "react";
import { Search, AlertTriangle, Check, Loader2, Sparkles, X, Download, Award } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { PageContainer, PageHeader } from "@/components/Page";

const sevColor = {
  high: "text-red-500 bg-red-50 border-red-200",
  medium: "text-orange-500 bg-orange-50 border-orange-200",
  low: "text-yellow-600 bg-yellow-50 border-yellow-200",
};
const sevDot = {
  high: "bg-red-500",
  medium: "bg-orange-500",
  low: "bg-yellow-500",
};

const WebsiteSEO = () => {
  const [url, setUrl] = useState("");
  const [report, setReport] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [fixIssue, setFixIssue] = useState(null);
  const [fixData, setFixData] = useState(null);
  const [loadingFix, setLoadingFix] = useState(false);

  const scan = async (e) => {
    e?.preventDefault();
    if (!url.trim()) return;
    setScanning(true); setReport(null);
    try {
      const { data } = await api.post("/seo/scan", { url });
      setReport(data);
    } catch { toast.error("Scan failed"); }
    finally { setScanning(false); }
  };

  const openFix = async (issue) => {
    setFixIssue(issue); setFixData(null); setLoadingFix(true);
    try {
      const { data } = await api.post("/seo/fix-suggestion", {
        issue_title: issue.title, issue_detail: issue.detail || "", url: report.url,
      });
      setFixData(data);
    } catch { toast.error("Fix generation failed"); setFixIssue(null); }
    finally { setLoadingFix(false); }
  };

  const downloadReport = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `seo-report-${new Date().toISOString().slice(0, 10)}.json`;
    a.click();
    toast.success("Report downloaded");
  };

  return (
    <PageContainer>
      <PageHeader title="SEO & AI Visibility" subtitle="See how you rank in search and AI tools." testId="seo-header"
        actions={report && <button onClick={downloadReport} className="op-btn-secondary flex items-center gap-1.5" data-testid="seo-download"><Download size={14} /> Download report</button>} />

      <div className="max-w-4xl mx-auto">
        <form onSubmit={scan} className="op-card p-6 mb-6" data-testid="seo-form">
          <h2 className="text-lg font-semibold text-gray-900 mb-2">Personalize your report</h2>
          <p className="text-sm text-gray-500 mb-4">Paste your website URL — we'll scan it and surface what to fix first.</p>
          <div className="flex gap-2">
            <input type="url" required value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://yourbrand.com" className="op-input flex-1" data-testid="seo-url-input" />
            <button type="submit" disabled={scanning} className="op-btn-primary flex items-center gap-1.5" data-testid="seo-scan-btn">
              {scanning ? <Loader2 size={16} className="animate-spin" /> : <Search size={16} />}
              {scanning ? "Scanning…" : "Start Free Scan →"}
            </button>
          </div>
        </form>

        {scanning && (
          <div className="op-card p-8" data-testid="seo-loading">
            <div className="flex items-center justify-center gap-3 text-gray-500">
              <Loader2 className="animate-spin" size={20} />
              <p>Auditing your site with AI… ~5–10s</p>
            </div>
            <div className="mt-6 space-y-2 max-w-md mx-auto">
              {["Crawling pages", "Checking metadata", "Analyzing AI visibility"].map((s) => (
                <div key={s} className="flex items-center gap-2 text-sm text-gray-400">
                  <Loader2 size={12} className="animate-spin" /> {s}
                </div>
              ))}
            </div>
          </div>
        )}

        {report && !scanning && (
          <div className="space-y-6" data-testid="seo-report">
            <div className="op-card p-6">
              <div className="flex items-start justify-between mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wide text-gray-500 font-semibold">Overall Score</p>
                  <p className="text-5xl font-bold text-[#7C3AED] mt-1">{report.score}<span className="text-2xl text-gray-400">/100</span></p>
                  <p className="text-sm text-gray-600 mt-2 max-w-lg">{report.summary}</p>
                </div>
                <div className="text-xs text-gray-400">Scanned {report.url}</div>
              </div>
              <div className="h-2 bg-gray-100 rounded-full mb-2 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-[#7C3AED] to-[#6D28D9] transition-all" style={{ width: `${report.score}%` }} />
              </div>
            </div>

            {report.wins?.length > 0 && (
              <div className="op-card p-6">
                <div className="flex items-center gap-2 mb-3">
                  <Award size={18} className="text-emerald-500" />
                  <h3 className="font-semibold text-gray-900">What you're doing well</h3>
                </div>
                <ul className="space-y-2">
                  {report.wins.map((w, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-700"><Check size={16} className="text-emerald-500 mt-0.5 flex-shrink-0" /> {w}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="op-card p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Top issues to fix</h3>
              <div className="space-y-3">
                {report.issues.map((iss, i) => (
                  <div key={iss.id || i} className={`p-4 rounded-lg border ${sevColor[iss.severity] || sevColor.low}`} data-testid={`seo-issue-${i}`}>
                    <div className="flex items-start gap-3">
                      <div className={`w-2 h-2 rounded-full mt-2 ${sevDot[iss.severity] || sevDot.low}`} />
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900">{iss.title}</p>
                          <span className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{iss.severity}</span>
                        </div>
                        {iss.detail && <p className="text-sm text-gray-600 mt-1">{iss.detail}</p>}
                        {iss.fix && <p className="text-xs text-gray-500 mt-2"><span className="font-semibold">Quick fix:</span> {iss.fix}</p>}
                        <button onClick={() => openFix(iss)} className="mt-2 text-xs text-[#7C3AED] font-semibold flex items-center gap-1 hover:underline" data-testid={`seo-fix-btn-${i}`}>
                          <Sparkles size={12} /> Get AI fix steps →
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="op-card p-6">
              <h3 className="font-semibold text-gray-900 mb-3">AI Visibility</h3>
              <p className="text-xs text-gray-500 mb-4">How discoverable you are in modern AI tools.</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Object.entries(report.ai_visibility || {}).map(([k, v]) => (
                  <div key={k} className="text-center p-4 bg-gray-50 rounded-lg" data-testid={`seo-ai-${k}`}>
                    <p className="text-xs text-gray-500 capitalize">{k.replace("_", " ")}</p>
                    <p className={`text-sm font-semibold mt-1 ${v === "Indexed" ? "text-emerald-600" : v === "Partial" ? "text-orange-500" : "text-gray-400"}`}>{v}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      {fixIssue && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setFixIssue(null)} data-testid="seo-fix-modal">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 shadow-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <p className="text-xs uppercase font-semibold text-gray-500">AI Fix · {fixIssue.severity}</p>
                <h2 className="text-lg font-bold mt-0.5">{fixIssue.title}</h2>
              </div>
              <button onClick={() => setFixIssue(null)}><X size={18} className="text-gray-400" /></button>
            </div>
            {loadingFix && (
              <div className="py-8 text-center text-gray-500"><Loader2 className="animate-spin mx-auto mb-2" size={20} /><p className="text-sm">Generating personalized fix…</p></div>
            )}
            {fixData && (
              <div>
                <div className="flex items-center gap-3 mb-4 text-xs">
                  <span className="px-2 py-1 rounded-full bg-purple-100 text-[#7C3AED] font-semibold">~{fixData.estimated_minutes} min</span>
                  <span className={`px-2 py-1 rounded-full font-semibold ${fixData.impact === "high" ? "bg-emerald-100 text-emerald-700" : fixData.impact === "medium" ? "bg-orange-100 text-orange-700" : "bg-gray-100 text-gray-600"}`}>{fixData.impact} impact</span>
                </div>
                <ol className="space-y-3">
                  {(fixData.steps || []).map((s, i) => (
                    <li key={i} className="flex gap-3" data-testid={`seo-fix-step-${i}`}>
                      <span className="w-6 h-6 rounded-full bg-[#7C3AED] text-white text-xs font-semibold flex items-center justify-center flex-shrink-0 mt-0.5">{i + 1}</span>
                      <p className="text-sm text-gray-700">{s}</p>
                    </li>
                  ))}
                </ol>
              </div>
            )}
          </div>
        </div>
      )}
    </PageContainer>
  );
};
export default WebsiteSEO;
